export const EMBED_PLATFORM_ID_PARAM = "embedPlatformId";
export const EMBED_PARENT_ORIGIN_PARAM = "embedParentOrigin";
export const EMBED_TOKEN_PARAM = "embedToken";
/**
 * What browsers report as an origin when none exists — `location.origin` of an
 * `about:srcdoc` frame (page-builder HTML blocks), a sandboxed frame without
 * `allow-same-origin`, a `data:` URL. It names no site, so it is never a parent origin.
 */
export const EMBED_OPAQUE_ORIGIN = "null";
/** Parent launcher listens for this message to reveal the iframe on mobile Safari. */
export const EMBED_READY_MESSAGE_TYPE = "ageless-embed-ready";
/**
 * Posted to the parent launcher when Chrome's Heavy Ad Intervention fires
 * inside the embed iframe (observed via ReportingObserver moments before the
 * frame is unloaded). The hosted loader responds by reloading the iframe once
 * with `EMBED_GATE_PARAM=1`, which re-enables the tap-to-start activation gate
 * on the fresh document (a new document gets a fresh pre-interaction budget,
 * and the tap permanently exempts the frame).
 */
export const EMBED_HEAVY_AD_UNLOAD_MESSAGE_TYPE = "ageless-embed-heavy-ad-unload";
/**
 * Query flag the hosted loader appends when reloading an iframe after a Heavy
 * Ad unload. Survives the mint redirect (only `embedParentOrigin` is
 * stripped), where the assessment layout reads it to arm
 * `WebsiteEmbedActivationGate`.
 */
export const EMBED_GATE_PARAM = "embedGate";
export const EMBED_GATE_PARAM_VALUE = "1";
const EMBED_PATH_PATTERN =
  /^\/embed\/platforms\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

export function buildEmbedAssessmentRedirectUrl(path: string, platformId: string): string | null {
  const url = new URL(path, "https://ageless.local");
  const segments = url.pathname.split("/").filter(Boolean);

  if (segments.length < 4 || segments[1] !== "a") {
    return null;
  }

  const subdomainSlug = segments[2];
  const flow = segments[3];
  const stepSegments = segments.slice(4);
  const stepPath = stepSegments.length > 0 ? `/${stepSegments.join("/")}` : "";

  return `/embed/platforms/${platformId}/a/${subdomainSlug}/${flow}${stepPath}${url.search}`;
}

export function extractEmbedPlatformId(pathname: string): string | null {
  const match = pathname.match(EMBED_PATH_PATTERN);
  return match?.[1]?.toLowerCase() ?? null;
}

/**
 * Why a bootstrap failure could not establish an embed session, derived from
 * the framing context. Drives targeted guidance on the bootstrap error page:
 * the generic "Invalid embed link." tells neither a practice owner testing the
 * raw URL nor one who pasted the snippet into Wix's sandboxed Embed-HTML
 * element what to do next, and both were multi-day support tickets in practice.
 */
export type EmbedBootstrapFailureContext =
  /** Opened directly in a browser tab — the embed URL only works inside an approved website. */
  | "top-level"
  /**
   * Framed by Wix's Embed-HTML sandbox (`*.filesusr.com`). Code pasted into
   * Wix's "Embed Code → Embed HTML" element runs on that Wix-owned host, so
   * the parent origin can never match the practice's allowlisted website —
   * and Wix's sandbox iframe also never delegates camera access. Permanently
   * broken; the fix is installing via Wix Settings → Custom Code.
   */
  | "wix-sandbox"
  /** Framed by the Wix editor/preview — the embed only loads on the published site. */
  | "wix-editor"
  | "unknown";

/**
 * Classifies why an embed bootstrap failed, from the framing context the
 * browser exposes. `candidateParentOrigins` accepts URLs or bare origins from
 * any channel that names a parent (the `embedParentOrigin` query param, the
 * document referrer, `location.ancestorOrigins` entries) — unparseable or
 * empty entries are ignored. Top-level wins over host classification: with no
 * parent frame at all, sandbox guidance would be wrong even if a stale query
 * param names one.
 */
export function classifyEmbedBootstrapFailureContext(
  isTopLevel: boolean,
  candidateParentOrigins: ReadonlyArray<string | null | undefined>,
): EmbedBootstrapFailureContext {
  if (isTopLevel) {
    return "top-level";
  }

  const hostnames = candidateParentOrigins.flatMap((candidate) => {
    if (!candidate) {
      return [];
    }
    try {
      return [new URL(candidate).hostname.toLowerCase()];
    } catch {
      return [];
    }
  });

  if (hostnames.some((hostname) => hostname.endsWith(".filesusr.com"))) {
    return "wix-sandbox";
  }

  if (
    hostnames.some(
      (hostname) => hostname === "editor.wix.com" || hostname.endsWith(".editor.wix.com"),
    )
  ) {
    return "wix-editor";
  }

  return "unknown";
}
